import { useEffect, useRef, useState } from "react";

import useSWR from "swr";
// imported from the context module rather than the barrel so no payment call site pulls in EmbedLayout
import { useEmbed } from "~/components/Embed/EmbedContext";

declare global {
    interface Window {
        handleCallbackPayment: any;
    }
}

interface CurrentPayment {
    waitingForPayment: boolean;
    callback: () => void;
    molliePaymentId: string;
}

interface PaymentResponse {
    redirectUrl?: string;
    molliePaymentId?: string;
}

interface RedirectToCheckoutProps extends PaymentResponse {
    callback: () => void;
    popupWindow?: Window;

    [x: string]: any;
}

interface PaymentHandlerReturn {
    handleError: (popup?: Window | null) => void;
    redirectToCheckout: (x: RedirectToCheckoutProps) => void;
    currentPayment: { waitingForPayment: boolean; callback: () => void; molliePaymentId: string };
    preparePopup: (timeoutCallback?: () => void, redirectUrl?: string, name?: string) => Window | null;
}

// drives a Mollie checkout from a separate window and resolves the caller once it is paid
export const usePaymentHandler = (
    windowName: string = "SportisionPaymentHandlerWindow",
    timeout: number = 30
): PaymentHandlerReturn => {
    const windowRef = useRef<Window>(undefined);
    const timeoutRef = useRef<NodeJS.Timeout>(undefined);
    // inside an iframe a popup window is unreliable and Mollie/PayPal/banks refuse to render
    // framed at all (X-Frame-Options), so the checkout gets a plain new tab instead.
    // An empty feature string opens a tab, "popup" opens a popup — everywhere else nothing changes.
    const { isEmbed } = useEmbed();
    const windowFeatures = isEmbed ? "" : "popup";
    const [currentPayment, setCurrentPayment] = useState<CurrentPayment>({
        waitingForPayment: false,
        callback: () => {
        },
        molliePaymentId: null
    });
    const { data, mutate } = useSWR(
        currentPayment.molliePaymentId ? `payment/${currentPayment.molliePaymentId}/status` : null,
        {
            refreshInterval: (status) => (status?.processed ? 0 : 2500)
        }
    );

    // re-checks the payment status; called by /mollie-connect/payment/[id] via window.opener
    const handleCallbackPayment = () => mutate();

    // close the checkout window again when the booking request itself failed
    const handleError = (popupWindow?: Window | null) => {
        popupWindow?.close();
    };

    // open the checkout target up front (inside the user gesture) so it is not blocked later.
    // no "noopener": /mollie-connect/payment/[id] calls back through window.opener.
    const preparePopup = (
        timeoutCallback: () => void | null = null,
        redirectUrl: string = "/mollie-connect/redirect",
        name: string = "SportisionPaymentHandlerPopup"
    ): Window | null => {
        if (timeoutCallback !== null) timeoutRef.current = setTimeout(timeoutCallback, timeout * 100);
        let popup = windowRef.current?.open(redirectUrl, name, windowFeatures);

        if (!popup || popup.closed || typeof popup.closed == "undefined") {
            let element = windowRef.current?.document.createElement("a");
            if (element) {
                element.setAttribute("href", redirectUrl);
                element.setAttribute("target", "_blank");
                windowRef.current?.document.body.appendChild(element);
                element.click();
                windowRef.current?.document.body.removeChild(element);
            }
        }

        return popup;
    };

    // send the (pre-opened) checkout window to Mollie and start polling the payment status
    const redirectToCheckout = ({
                                    redirectUrl,
                                    molliePaymentId,
                                    callback,
                                    popupWindow = null
                                }: RedirectToCheckoutProps) => {
        if (redirectUrl && molliePaymentId) {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            setCurrentPayment({ molliePaymentId, callback, waitingForPayment: true });

            if (!popupWindow) {
                preparePopup(null, redirectUrl, "SportisionPaymentHandlerPopup_" + molliePaymentId);
            } else {
                // handle pre opened popup
                popupWindow.location = redirectUrl;
                popupWindow.name = "SportisionPaymentHandlerPopup_" + molliePaymentId;
            }
        } else {
            popupWindow?.close();
            callback();
        }
    };

    useEffect(() => {
        if (data?.processed) {
            setCurrentPayment({
                waitingForPayment: false, callback: () => {
                }, molliePaymentId: null
            });
            if (data?.succeed) {
                currentPayment.callback();
            }
        }
    }, [data]);

    useEffect(() => {
        if (window) {
            window.name = windowName;
            window.handleCallbackPayment = handleCallbackPayment;

            windowRef.current = window;
        }
    });

    return { redirectToCheckout, currentPayment, preparePopup, handleError };
};
